/**
 * @documental
 * @export Icon
 * @import
 * import { Icon } from "capitalrx-components-2.0/lib/general";
 */
import { CSSProperties, FC, ReactNode, RefCallback, useMemo } from "react";

import { AccessibilityProps } from "../../AccessibilityProps";
import { createComponentStyleHook } from "../../tokens";
import classNames from "classnames";

import { BaseProps } from "../../BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import * as Color from "../Color";
import { Tooltip } from "../Tooltip";
import { IconColor } from "./IconColor";
import { IconName } from "./IconName";
import { IconSize } from "./IconSize";
import { formatIconName } from "./formatIconName";
import { getIconColorCssValue } from "./getIconColorCssValue";
import { getIconComponent } from "./getIconComponent";

import legacyStyles from "./legacy/styles.module.scss";
import tokenStyles from "./styles.module.css";

/**
 * The `Icon` component properties.
 */

export interface IconProps extends BaseProps, Pick<AccessibilityProps, "aria-label" | "aria-hidden"> {
  /**
   * The icon name. See below for a list of available icons and their names.
   */
  readonly name: IconName;

  /**
   * The size of the icon. If not specified defaults to `"regular"`.
   * icons defaults to regular size
   */
  readonly size?: IconSize;

  /**
   * The icon color. If not specified, uses the design token default in token mode
   * or DarkGrey[500] in legacy mode.
   */
  readonly color?: IconColor | Color.RgbColor;

  /**
   * The icon tooltip.
   */
  readonly tooltip?: ReactNode;

  /**
   * Indicates whether the icon is decorative or conveys meaning. Defaults to `false`.
   */
  readonly isDecorative?: boolean;
}

/**
 * NOTE: This component uses `createComponentStyleHook` for dual legacy/token
 * mode support. Other branches (e.g. Table migration) may still use
 * styled-components. This token-hook pattern is the canonical approach;
 * `reconcile any conflicts by preserving this architecture.
 */
const useResolvedIconStyles = createComponentStyleHook<IconProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "icon",
});

/**
 * Displays a scalable icon with the specified size and color.
 */
export const Icon: FC<IconProps> = (props) => {
  const {
    name,
    size = "regular",
    color,
    tooltip,
    "aria-label": ariaLabel,
    "aria-hidden": ariaHidden = false,
    isDecorative = false,
  } = props;
  const { mode, isToken, styles, StyleModeBoundary } = useResolvedIconStyles(props);

  let colorCssValue: string | undefined;
  if (color != null) {
    colorCssValue = getIconColorCssValue(color, isToken);
  }

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [
      styles.Icon,
      "icon",
      `size-${size}`,
      {
        "has-tooltip": !!tooltip,
      },
    ],
    testIdPrefix: "icon",
  });

  const Component = name !== "none" ? getIconComponent(name, size) : null;
  const computedRole = isDecorative ? "presentation" : "img";
  const computedLabel = ariaLabel || (name ? `${formatIconName(name)} Icon` : "Icon");
  const computedVisibility = isDecorative ? true : (ariaHidden ?? false);

  const containerStyle = useMemo(
    () => (colorCssValue != null ? ({ "--icon-fill-color": colorCssValue } as CSSProperties) : undefined),
    [colorCssValue],
  );

  const content = (ref?: RefCallback<HTMLElement>) => (
    <span ref={ref} {...baseHtmlProps}>
      <span className={classNames(styles.IconContainer, "icon-container")} style={containerStyle}>
        {name !== "none" && Component !== null && (
          <Component title={computedLabel} aria-hidden={computedVisibility} role={computedRole} focusable="false" />
        )}
      </span>
    </span>
  );

  return (
    <StyleModeBoundary mode={mode}>
      {tooltip && <Tooltip content={tooltip}>{content}</Tooltip>}
      {!tooltip && content()}
    </StyleModeBoundary>
  );
};
